import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { supabase } from '@/integrations/supabase/client';
import { useGameStore } from '@/lib/gameStore';
import { Button } from './ui/button';
import PlayerAvatar from './PlayerAvatar';
import GameResultCard from './GameResultCard';
import XPReward from './XPReward';
import { useStreaks } from '@/hooks/useStreaks';
import { useXP } from '@/hooks/useXP';
import { useRematchRequest } from '@/hooks/useRematchRequest';
import { RematchRequestModal } from './RematchRequestModal';

// Victory animation component - 6 and 7 moving in opposite directions
function SixSevenAnimation() {
  return (
    <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center overflow-visible">
      <div className="relative flex items-center justify-center gap-4 w-full max-w-lg h-80">
        {/* Number 6 - moves UP */}
        <motion.div
          className="text-[10rem] md:text-[14rem] font-black text-chaos-green select-none leading-none"
          style={{ 
            textShadow: '0 0 40px hsl(var(--chaos-green) / 0.8), 0 0 80px hsl(var(--chaos-green) / 0.4)',
          }}
          initial={{ y: 0, opacity: 0 }}
          animate={{ 
            y: [-30, -80, -30],
            opacity: [0, 1, 1, 0.9, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: [0.45, 0, 0.55, 1]
          }}
        >
          6
        </motion.div>
        
        {/* Number 7 - moves DOWN */}
        <motion.div
          className="text-[10rem] md:text-[14rem] font-black text-chaos-green select-none leading-none"
          style={{ 
            textShadow: '0 0 40px hsl(var(--chaos-green) / 0.8), 0 0 80px hsl(var(--chaos-green) / 0.4)',
          }}
          initial={{ y: 0, opacity: 0 }}
          animate={{ 
            y: [30, 80, 30],
            opacity: [0, 1, 1, 0.9, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: [0.45, 0, 0.55, 1]
          }}
        >
          7
        </motion.div>
      </div>
    </div>
  );
}

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';

interface GameEndProps {
  onPlayAgain: () => void;
  onBackToMenu: () => void;
  setMusicIntensity: (intensity: MusicIntensity) => void;
  onRematchAccepted?: (newRoomId: string) => void;
}

export default function GameEnd({ onPlayAgain, onBackToMenu, setMusicIntensity, onRematchAccepted }: GameEndProps) {
  const { currentRoom, playerId, playerName } = useGameStore();
  const [gameHistory, setGameHistory] = useState<any[]>([]);
  const [mvp, setMvp] = useState<any>(null);
  const [chaosAgent, setChaosAgent] = useState<any>(null);
  const [xpAwarded, setXpAwarded] = useState(false);
  const [xpBreakdown, setXpBreakdown] = useState<any>(null);
  const [leveledUp, setLeveledUp] = useState(false);
  const [previousLevel, setPreviousLevel] = useState(1);
  const [newLevel, setNewLevel] = useState(1);
  const [rematchDismissed, setRematchDismissed] = useState(false);
  
  const { streakData, recordGame, getCompletedChallengeRewards } = useStreaks();
  const { xpData, calculateGameXP, awardXP, getLevelProgress } = useXP();
  
  const xpAwardedRef = useRef(false);

  // Rematch request handling
  const handleRematchAccepted = (newRoomId: string) => {
    if (onRematchAccepted) {
      onRematchAccepted(newRoomId);
    }
  };

  const {
    activeRequest,
    votes,
    hasVoted,
    initiateRematch,
    castVote,
    timeRemaining,
  } = useRematchRequest(
    currentRoom?.id || null,
    playerId,
    playerName,
    handleRematchAccepted
  );

  // Reset dismiss state when a new request comes in
  useEffect(() => {
    if (activeRequest) {
      setRematchDismissed(false);
    }
  }, [activeRequest?.id]);

  const isWin = currentRoom?.currentTotal === currentRoom?.targetTotal;

  // Set victory or defeat music intensity
  useEffect(() => {
    setMusicIntensity(isWin ? 'victory' : 'defeat');
  }, [isWin, setMusicIntensity]);

  useEffect(() => {
    if (!currentRoom) return;

    // Load game history
    const loadHistory = async () => {
      const { data } = await supabase
        .from('game_history')
        .select('*')
        .eq('room_id', currentRoom.id)
        .order('round_number', { ascending: true });

      if (data) {
        setGameHistory(data);
        calculateMVP(data);
      }
    };

    loadHistory();

    // Trigger confetti for wins
    if (isWin) {
      const duration = 3000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b']
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b']
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();
    }
  }, [currentRoom?.id, isWin]);

  const calculateMVP = (history: any[]) => {
    if (!currentRoom) return;

    const playerContributions = new Map();

    history.forEach((round) => {
      const selections = round.selections || [];
      selections.forEach((selection: any) => {
        if (!playerContributions.has(selection.player_id)) {
          playerContributions.set(selection.player_id, { good: 0, bad: 0 });
        }

        const contrib = playerContributions.get(selection.player_id);
        if (round.moved_closer) {
          contrib.good++;
        } else {
          contrib.bad++;
        }
      });
    });

    // Find MVP (most good contributions)
    let mvpId = null;
    let maxGood = -1;

    // Find Chaos Agent (most bad contributions)
    let chaosId = null;
    let maxBad = -1;

    playerContributions.forEach((contrib, pid) => {
      if (contrib.good > maxGood) {
        maxGood = contrib.good;
        mvpId = pid;
      }
      if (contrib.bad > maxBad) {
        maxBad = contrib.bad;
        chaosId = pid;
      }
    });

    const mvpPlayer = currentRoom.players.find(p => p.id === mvpId);
    const chaosPlayer = currentRoom.players.find(p => p.id === chaosId);

    setMvp(mvpPlayer);
    setChaosAgent(chaosPlayer);
    
    // Award XP after calculating MVP (only once)
    if (!xpAwardedRef.current && currentRoom) {
      xpAwardedRef.current = true;
      
      const isMVP = mvpId === playerId;
      const rounds = currentRoom.currentRound;
      const players = currentRoom.players.length;
      
      // Record the game for streak tracking
      recordGame(isWin, rounds);
      
      // Calculate XP
      const streakBonus = streakData.currentStreak >= 3 ? streakData.currentStreak * 10 : 0;
      const challengeBonus = getCompletedChallengeRewards();
      
      const breakdown = calculateGameXP({
        won: isWin,
        isMVP,
        rounds,
        players,
        streakBonus,
        challengeBonus,
      });
      
      setXpBreakdown(breakdown);
      setPreviousLevel(xpData.level);
      
      // Award XP
      const result = awardXP(breakdown.total);
      setLeveledUp(result.leveledUp);
      setNewLevel(result.newLevel);
      setXpAwarded(true);
    }
  };

  const handlePlayAgain = async () => {
    if (!currentRoom) return;
    
    // Use rematch voting system instead of direct reset
    await initiateRematch();
  };

  if (!currentRoom) return null;

  const totalActivePlayers = currentRoom.players.filter(p => p.isActive).length;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8 relative">
      {/* Victory 6-7 Animation */}
      {isWin && <SixSevenAnimation />}
      
      {/* Rematch Request Modal */}
      {activeRequest && !rematchDismissed && (
        <RematchRequestModal
          initiatorName={activeRequest.initiator_name}
          votes={votes}
          totalPlayers={totalActivePlayers}
          hasVoted={hasVoted}
          timeRemaining={timeRemaining}
          onVoteYes={() => castVote(true)}
          onVoteNo={() => castVote(false)}
          onDismiss={() => setRematchDismissed(true)}
          status={activeRequest.status}
        />
      )}

      <motion.div
        className="max-w-4xl mx-auto space-y-8"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Hero Section */}
        <div className="text-center space-y-4">
          <motion.div
            className="text-9xl font-black text-shadow"
            animate={{ 
              scale: [1, 1.05, 1],
              rotate: isWin ? [0, 5, -5, 0] : 0
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {isWin ? '🎉' : '💀'}
          </motion.div>
          
          <h1 className={`text-6xl md:text-8xl font-black ${
            isWin ? 'text-chaos-green' : 'text-chaos-red'
          } text-shadow`}>
            {isWin ? 'YOU DID IT!' : 'GAME OVER'}
          </h1>

          <p className="text-2xl font-bold text-muted-foreground">
            {isWin ? 'Chaos conquered! 🔥' : 'The chaos was too much... 😔'}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass-card p-6 rounded-2xl text-center">
            <div className="text-sm font-bold text-muted-foreground mb-2">FINAL TOTAL</div>
            <div className="text-4xl font-black text-primary">{currentRoom.currentTotal}</div>
          </div>
          
          <div className="glass-card p-6 rounded-2xl text-center">
            <div className="text-sm font-bold text-muted-foreground mb-2">TARGET</div>
            <div className="text-4xl font-black text-chaos-green">{currentRoom.targetTotal}</div>
          </div>
          
          <div className="glass-card p-6 rounded-2xl text-center">
            <div className="text-sm font-bold text-muted-foreground mb-2">ROUNDS</div>
            <div className="text-4xl font-black text-foreground">{currentRoom.currentRound}</div>
          </div>
          
          <div className="glass-card p-6 rounded-2xl text-center">
            <div className="text-sm font-bold text-muted-foreground mb-2">PLAYERS</div>
            <div className="text-4xl font-black text-foreground">{currentRoom.players.length}</div>
          </div>
        </div>

        {/* XP Reward */}
        {xpAwarded && xpBreakdown && (
          <XPReward
            breakdown={xpBreakdown}
            previousLevel={previousLevel}
            newLevel={newLevel}
            leveledUp={leveledUp}
            levelProgress={getLevelProgress()}
          />
        )}

        {/* MVP & Chaos Agent */}
        <div className="grid md:grid-cols-2 gap-6">
          {mvp && (
            <motion.div
              className="glass-card p-6 rounded-3xl border-2 border-chaos-green/50"
              whileHover={{ scale: 1.02 }}
            >
              <div className="text-center space-y-4">
                <div className="text-4xl">🏆</div>
                <div className="text-2xl font-black text-chaos-green">MVP</div>
                <PlayerAvatar player={mvp} size="lg" />
                <div className="text-xl font-bold text-foreground">{mvp.name}</div>
                <div className="text-sm text-muted-foreground">Made the clutch plays! 🔥</div>
              </div>
            </motion.div>
          )}

          {chaosAgent && (
            <motion.div
              className="glass-card p-6 rounded-3xl border-2 border-chaos-red/50"
              whileHover={{ scale: 1.02 }}
            >
              <div className="text-center space-y-4">
                <div className="text-4xl">💀</div>
                <div className="text-2xl font-black text-chaos-red">CHAOS AGENT</div>
                <PlayerAvatar player={chaosAgent} size="lg" />
                <div className="text-xl font-bold text-foreground">{chaosAgent.name}</div>
                <div className="text-sm text-muted-foreground">Kept things spicy! 🌶️</div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Share Result Card */}
        <GameResultCard
          isWin={isWin}
          finalTotal={currentRoom.currentTotal}
          targetTotal={currentRoom.targetTotal}
          rounds={currentRoom.currentRound}
          players={currentRoom.players.length}
          mvpName={mvp?.name}
          chaosAgentName={chaosAgent?.name}
          roomCode={currentRoom.id}
        />

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <Button
            onClick={handlePlayAgain}
            size="lg"
            className="text-2xl px-12 py-6 h-auto font-black rounded-2xl"
          >
            🔄 PLAY AGAIN
          </Button>
          
          <Button
            onClick={onBackToMenu}
            size="lg"
            variant="outline"
            className="text-2xl px-12 py-6 h-auto font-black rounded-2xl"
          >
            🏠 BACK TO MENU
          </Button>
        </div>
      </motion.div>
    </div>
  );
}